import useScrollIntoView from '@/hooks/useScrollIntoView';
import DropdownButton from '@/storybook/components/FormCollection/components/DropdownButton/DropdownButton';
import DropdownList from '@/storybook/components/FormCollection/components/DropdownList/DropdownList';
import { useFloatingDropdownPanel } from '@/storybook/components/FormCollection/components/DropdownList/useFloatingDropdownPanel';
import DropdownRatingPanel from '@/storybook/components/FormCollection/components/DropdownRatingPanel/DropdownRatingPanel';
import VirtualizedRows from '@/storybook/components/FormCollection/components/VirtualizedRows/VirtualizedRows';
import {
  createStableObjectHash,
  DropdownPosition,
  getFixedOptions,
  handleDropdownListPosition,
} from '@/storybook/components/FormCollection/utils/utils';
import { SelectItem } from '@/types/ui';
import cx from 'classnames';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './MultiLevelSelectFloatingLabel.module.css';
import { useMultiLevelSelectButtonValue } from './MultiLevelSelectFloatingLabel/hooks/useMultiLevelSelectButtonValue';
import { useMultiLevelSelectHandlersFloatingLabel } from './MultiLevelSelectFloatingLabel/hooks/useMultiLevelSelectHandlersFloatingLabel';
import { useMultiLevelSelectOptionsFloatingLabel } from './MultiLevelSelectFloatingLabel/hooks/useMultiLevelSelectOptionsFloatingLabel';
import { useMultiLevelSelectVirtualizationFloatingLabel } from './MultiLevelSelectFloatingLabel/hooks/useMultiLevelSelectVirtualizationFloatingLabel';
import { MultiLevelSelectFloatingLabelProps } from './MultiLevelSelectFloatingLabel/types/MultiLevelSelectFloatingLabel.types';

export default function MultiLevelSelectFloatingLabel<T>({
  label,
  tooltip,
  options = [],
  unfilteredOptions,
  isLoading,
  value = [],
  onChange,
  placeholder,
  disabled = false,
  className,
  clearable = true,
  searchable,
  adobeEventTrackFunctions,
  onDropdownToggle,
  alignment = 'vertical',
  'data-testid': dataTestId = 'multiLevelSelectFloatingLabel',
  dropdownButtonTestId,
  dropdownListTestId,
  dropdownPosition,
  size = 'L',
  state = 'default',
}: MultiLevelSelectFloatingLabelProps<T>) {
  const [dropdownListPosition, setDropdownListPosition] = useState<DropdownPosition>(dropdownPosition ?? 'down');
  const [selectedOption, setSelectedOption] = useState<SelectItem<T> | undefined>(undefined);
  const [searchInput, setSearchInput] = useState<string | undefined>();
  const [memoizedValueList, setMemoizedValueList] = useState<SelectItem<T>[]>([]);
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const { t } = useTranslation();

  const dropdownButtonRef = useRef<HTMLDivElement>(null);
  const { dropdownRef, panelRef } = useFloatingDropdownPanel({ isOpen, onClose: () => setIsOpen(false) });

  useScrollIntoView({
    elementRef: dropdownRef,
    isVisible: isOpen,
  });

  const handleToggleDropdown = useCallback(() => {
    if (disabled) return;
    setIsOpen(prev => {
      const newState = !prev;
      onDropdownToggle?.(newState);
      if (newState) {
        handleDropdownListPosition(dropdownPosition, dropdownRef, setDropdownListPosition);
        setSearchInput(undefined);
        setSelectedOption(undefined);
        setMemoizedValueList(value);
      }
      return newState;
    });
  }, [disabled, dropdownPosition, dropdownRef, onDropdownToggle, value]);

  const { isSelected, searchResults, selectedOptions, notSelectedOptions, categoriesToDisplay } =
    useMultiLevelSelectOptionsFloatingLabel({
      options,
      unfilteredOptions,
      value,
      memoizedValueList,
      searchInput,
    });

  const { handleClearValue, handleClickOnCheckbox, handleSelectLevel } = useMultiLevelSelectHandlersFloatingLabel({
    value,
    onChange,
    disabled,
    clearable,
    setSelectedOption,
  });

  const { virtualizedList, parentRef, renderVirtualRow } = useMultiLevelSelectVirtualizationFloatingLabel({
    selectedOptions,
    notSelectedOptions,
    categoriesToDisplay,
    noCategoryLabel: t('components.dropdownPanel.noCategory'),
    handleClickOnCheckbox,
    isSelected,
    value,
    setSelectedOption,
  });

  const dropdownButtonFixedOptions = useMemo(
    () => getFixedOptions(options, unfilteredOptions),
    [options, unfilteredOptions],
  );

  const optionsMap = useMemo(() => {
    const map = new Map<string, SelectItem<T>>();
    dropdownButtonFixedOptions.forEach(opt => {
      const key =
        typeof opt.value === 'object' && opt.value !== null ? createStableObjectHash(opt.value) : String(opt.value);
      map.set(key, opt);
    });
    return map;
  }, [dropdownButtonFixedOptions]);

  const dropdownButtonValue = useMultiLevelSelectButtonValue({
    value,
    optionsMap,
  });

  const displayLabel = label || placeholder;
  const isLabelFloating = value.length > 0 || isOpen;

  return (
    <div
      ref={dropdownRef}
      className={cx(styles.floatingLabelContainer, className)}
      data-alignment={alignment}
      data-testid={dataTestId}
      onClick={adobeEventTrackFunctions}
      data-state={state}
    >
      <section className={styles.dropdownContainer}>
        {displayLabel && (
          <label className={styles.floatingLabel} data-floating={isLabelFloating} data-has-value={value.length > 0}>
            {displayLabel}
          </label>
        )}
        <div className={styles.buttonWrapper}>
          <DropdownButton
            ref={dropdownButtonRef}
            size={size}
            type="multiLevelSelect"
            placeholder=""
            numOfValue={value.length}
            active={isOpen}
            onClick={!disabled ? handleToggleDropdown : undefined}
            onClear={handleClearValue}
            clearable={clearable}
            disabled={disabled}
            className={styles.dropdownButton}
            data-testid={dropdownButtonTestId}
          >
            <div className={styles.value}>
              {dropdownButtonValue ? (
                <span className={styles.valueText}>{dropdownButtonValue}</span>
              ) : isLabelFloating && placeholder ? (
                <span className={cx(styles.valueText, styles.placeholder)}>{placeholder}</span>
              ) : null}
            </div>
          </DropdownButton>
        </div>
        <DropdownList
          isOpen={isOpen}
          isLoading={isLoading}
          disabled={disabled}
          position={dropdownListPosition}
          isSearchable={!selectedOption ? searchable : undefined}
          searchValue={searchInput}
          onSearchValueChange={setSearchInput}
          numberOfSearchResults={searchResults.length}
          data-testid={dropdownListTestId}
          listRef={parentRef}
          dropdownTriggerRef={dropdownButtonRef}
          isFloating
          panelRef={panelRef}
          optionsCount={options.length}
        >
          {selectedOption ? (
            <DropdownRatingPanel
              label={selectedOption.label}
              onClick={(level: number) => handleSelectLevel(selectedOption, level)}
              onClose={() => setSelectedOption(undefined)}
              data-testid="multiLevelSelectFloatingLabelRatingPanel"
            />
          ) : (
            <VirtualizedRows virtualization={virtualizedList} renderRow={renderVirtualRow} />
          )}
        </DropdownList>
      </section>
      {tooltip && <div className={styles.tooltip}>{tooltip}</div>}
    </div>
  );
}
